/**
 * ReconcileCheckCard — the Onfleet side row of the delivered pane's reconcile
 * card (#2484; the row was born in the dispatch checklist, #1676). It used to render nothing at all until the operator
 * clicked 对账, so a day that had never been reconciled looked identical to one
 * with nothing to reconcile. Now it keeps its row and says "not run yet".
 */
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/types/api.generated';
import { CollapsibleCheck, ReconcileBadge } from './CollapsibleCheck';

type ReconcileReport = components['schemas']['ReconcileReport'];
type ReconcileCategory = ReconcileReport['diffs'][number]['category'];

const ALERT_CATEGORIES = new Set<ReconcileCategory>([
  'missing_ours',
  'missing_gas',
  'job_count_diverged',
]);
const REAL_DIFF_ORDER_CAP = 10;

interface ReconcileCheckCardProps {
  date: string;
  /** False = no reconcile has been run for the selected date yet. */
  triggered: boolean;
  report: ReconcileReport | undefined;
  error: unknown;
  isPending: boolean;
}

export function ReconcileCheckCard({
  date,
  triggered,
  report,
  error,
  isPending,
}: ReconcileCheckCardProps) {
  const { t } = useTranslation();
  const errorMessage = error instanceof Error ? error.message : t('error.unknown');
  const isOnfleetNotReady = report
    ? report.summary.total_gas === 0 && report.summary.total_ours > 0
    : false;
  const alertCount = report
    ? report.summary.missing_ours + report.summary.missing_gas + report.summary.job_count_diverged
    : 0;
  const realDiffOrderNames = report
    ? [
        ...new Set(
          report.diffs
            .filter((diff) => ALERT_CATEGORIES.has(diff.category))
            .map((diff) => diff.order_name),
        ),
      ]
    : [];
  const visibleRealDiffOrderNames = realDiffOrderNames.slice(0, REAL_DIFF_ORDER_CAP);
  const hiddenRealDiffOrderCount = realDiffOrderNames.length - visibleRealDiffOrderNames.length;

  return (
    <CollapsibleCheck
      testId="dispatch-reconcile-card"
      variant="row"
      title={t('dispatch.delivered.reconcile.onfleetSide')}
      description={t('dispatch.delivered.reconcile.onfleetWhy')}
      // Onfleet not having built the day's tasks yet is the normal morning
      // state, not a finding — it stays folded like any other passing check.
      defaultOpen={triggered && !isPending && (error !== undefined || alertCount > 0)}
      badge={
        <ReconcileBadge
          triggered={triggered}
          isFetching={isPending}
          isError={error !== undefined}
          alertCount={alertCount}
          alertLabel={t('dispatch.reconcile.diffLabel', { count: alertCount })}
          okLabel={t('dispatch.delivered.reconcile.match')}
        />
      }
    >
      <div className="space-y-3">
        {!triggered ? (
          <p className="text-sm text-muted-foreground">{t('dispatch.reconcile.idle')}</p>
        ) : isPending ? (
          <p className="text-sm text-muted-foreground">{t('dispatch.reconcile.running')}</p>
        ) : error ? (
          <p role="alert" className="text-sm text-destructive">
            {t('dispatch.reconcile.error', { message: errorMessage })}
          </p>
        ) : report ? (
          <>
            {isOnfleetNotReady ? (
              <p className="rounded-md border bg-muted/50 px-3 py-3 text-sm text-muted-foreground">
                {t('dispatch.reconcile.onfleetNotReady', {
                  date,
                  count: report.summary.total_ours,
                })}
              </p>
            ) : alertCount > 0 ? (
              <div
                role="alert"
                className="rounded-md border border-warning/40 bg-warning/10 px-3 py-3 text-warning"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">
                      {t('dispatch.reconcile.realDiffSummary', {
                        count: alertCount,
                        date,
                      })}
                    </p>
                    {report.summary.missing_ours > 0 && (
                      <>
                        <p className="mt-1 text-sm">
                          {t('dispatch.reconcile.warning', {
                            count: report.summary.missing_ours,
                            date,
                          })}
                        </p>
                        <p className="mt-1 text-sm">{t('dispatch.reconcile.backfillGuidance')}</p>
                      </>
                    )}
                    {visibleRealDiffOrderNames.length > 0 && (
                      <p data-testid="dispatch-reconcile-real-diffs" className="mt-2 text-sm">
                        {t('dispatch.reconcile.realDiffOrders')}:{' '}
                        {visibleRealDiffOrderNames.join(', ')}
                        {hiddenRealDiffOrderCount > 0
                          ? t('dispatch.reconcile.more', { count: hiddenRealDiffOrderCount })
                          : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="flex items-center gap-2 font-semibold text-foreground">
                  <CheckCircle className="h-5 w-5 text-success" aria-hidden="true" />
                  {t('dispatch.reconcile.passed')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('dispatch.reconcile.totals', {
                    ours: report.summary.total_ours,
                    gas: report.summary.total_gas,
                  })}
                </p>
              </div>
            )}
            {report.summary.cancelled_ours > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('dispatch.reconcile.cancelledOurs', {
                  count: report.summary.cancelled_ours,
                })}
              </p>
            )}
            {report.summary.out_of_band > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('dispatch.reconcile.outOfBand', {
                  count: report.summary.out_of_band,
                })}
              </p>
            )}
            {!isOnfleetNotReady && (
              <p className="text-xs text-muted-foreground">
                {t('dispatch.reconcile.secondary', {
                  missingGas: report.summary.missing_gas,
                  diverged: report.summary.job_count_diverged,
                })}
              </p>
            )}
          </>
        ) : null}
      </div>
    </CollapsibleCheck>
  );
}
